import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { OrderContext } from 'contexts/OrderContext';

import { usePriceVisible } from 'hooks/usePriceVisible';

import { formatDate } from 'helpers/formatDate';
import { formatOrderPrice } from 'helpers/parsePrice';

import { Headline } from 'components/Headline';

import { Actions } from '../Actions';
import * as S from './OrderHeader.styles';

/** Creation time with seconds, as in the order page mockup. */
const CREATION_TIME_FORMAT = 'HH:mm:ss';

const OrderHeader = (): React.ReactElement => {
  const { t } = useTranslation();
  const { orderHeader, orderTotal } = useContext(OrderContext);
  const priceVisible = usePriceVisible();

  return (
    <S.Container>
      <S.HeaderTop marginBottom="2rem">
        <Headline
          textTransform="uppercase"
          fontWeight="bold"
          scale={['xl2', 'xl']}
          color="blackLight"
          fontFamily="LFT Zeno Condensed"
        >
          {t('order.manage')} {orderHeader?.orderNumber}
        </Headline>
        <S.HeaderRight>
          <Actions noPadding />
        </S.HeaderRight>
      </S.HeaderTop>
      <S.MetaRow>
        <S.MetaBlock>
          <S.MetaKey>{t('order.number')}</S.MetaKey>
          <S.MetaValue $bold $wide>
            {orderHeader?.orderNumber}
          </S.MetaValue>
        </S.MetaBlock>

        <S.MetaBlock>
          <S.MetaKey>{t('orderHeader.creationDate')}</S.MetaKey>
          <S.MetaValue>{formatDate(orderHeader?.createdAt)}</S.MetaValue>
        </S.MetaBlock>

        <S.MetaBlock>
          <S.MetaKey>{t('orderHeader.creationTime')}</S.MetaKey>
          <S.MetaValue>{formatDate(orderHeader?.createdAt, CREATION_TIME_FORMAT)}</S.MetaValue>
        </S.MetaBlock>

        {priceVisible && (
          <S.MetaBlock leftMargin="auto" $align="end">
            <S.MetaKey>{t('orderTable.footer.totalAmount')}</S.MetaKey>
            <S.MetaValue $bold $wide>
              {formatOrderPrice(orderTotal.currency, orderTotal.price)}
            </S.MetaValue>
          </S.MetaBlock>
        )}

        <S.MetaBlock leftMargin={priceVisible ? undefined : 'auto'} $align="end">
          <S.MetaKey>{t('orderTable.footer.totalQuantity')}</S.MetaKey>
          <S.MetaValue $bold>{orderTotal.quantity}</S.MetaValue>
        </S.MetaBlock>
      </S.MetaRow>
    </S.Container>
  );
};

export { OrderHeader };
